import type { RequestArgs } from '@ff-it/api';
import type { MarkdownWrite } from 'components/RichText';
import { api, type RealtimeEvent } from 'services';
import {
  atEnd,
  commentReducer,
  createCommentState,
  hasNewer,
  hasOlder,
  type CommentAction,
  type CommentState,
  type CreateResponse,
  type DeleteResponse,
  type Edge,
  type EmbeddedTopic,
  type PageResponse,
  type SummaryResponse,
} from './commentStore';
import {
  buildCommentSummaryUrl,
  buildCommentUrl,
  buildDeleteCommentUrl,
  buildInitialCommentUrl,
  buildNewerCommentUrl,
  buildOlderCommentUrl,
  COMMENT_PAGE_SIZE,
} from './commentRequests';
import { planSync, type SyncStep } from './syncPlan';

function requestError(args: RequestArgs): Error {
  return new Error(`API Request Failed: ${args.method} ${args.url}`);
}

/** The two transitions that discard a window, and so release every request slot. */
type WindowAction = { type: 'OPEN'; anchor: number | null } | { type: 'CLOSE' };

/**
 * One comment topic: its state and all HTTP activity against it.
 *
 * Owning the state outright is what keeps this simple. `getState()` is
 * synchronous, so the reconciliation loop below reads the result of its own
 * previous step without waiting for a React commit — there is nothing to defer,
 * and no mirror of the state to keep in step.
 */
export class CommentTopicStore {
  readonly objectKey: string;

  private state: CommentState;
  private listeners = new Set<() => void>();

  /** In-flight pages, by edge. Both the single-flight guard and the aborter. */
  private pages: Partial<Record<Edge, AbortController>> = {};
  /** Deliberately outside `state.loading`: a background refresh owns no edge. */
  private summary: AbortController | null = null;
  private creating: AbortController | null = null;

  private running = false;
  private queued = false;
  private tailHole = false;
  private subscribedOnce = false;

  constructor(topic: EmbeddedTopic) {
    this.objectKey = topic.object_key;
    this.state = commentReducer(createCommentState(), { type: 'SEED', topic });
  }

  getState = (): CommentState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private dispatch(action: CommentAction): void {
    const next = commentReducer(this.state, action);
    // The reducer returns the same object when an action told it nothing new.
    // Such an action may not wake a subscriber.
    if (next === this.state) {
      return;
    }
    this.state = next;
    for (const listener of this.listeners) {
      listener();
    }
  }

  // -- lifecycle -----------------------------------------------------------

  seed(topic: EmbeddedTopic): void {
    this.dispatch({ type: 'SEED', topic });
  }

  /** Mount a stream: build a window, and stop counting arrivals. */
  open(anchor: number | null): void {
    void this.rebuild(anchor);
  }

  /** Unmount a stream. Nothing in flight may reach the store afterwards. */
  close(): void {
    this.creating?.abort();
    this.creating = null;
    this.queued = false;
    this.reset({ type: 'CLOSE' });
  }

  /**
   * A completed channel subscription session. The first is the one an open has
   * already rebuilt for, including when another consumer had this shared channel
   * subscribed before we appeared. A later session means events fired while we
   * were not listening.
   */
  resubscribed(): void {
    if (!this.subscribedOnce) {
      this.subscribedOnce = true;
      return;
    }
    if (this.state.open) {
      void this.rebuild(this.state.anchor);
    }
  }

  // -- the reconciliation pump ---------------------------------------------

  /** Ask for a reconciliation pass. Runs now if it can, otherwise when the wire drains. */
  sync = (): void => {
    this.queued = true;
    this.pump();
  };

  private busy(): boolean {
    return Object.keys(this.pages).length > 0 || this.summary !== null || this.creating !== null;
  }

  private pump(): void {
    if (this.running || !this.queued || this.busy()) {
      return;
    }
    void this.runLoop();
  }

  private async runLoop(): Promise<void> {
    this.running = true;
    try {
      while (this.queued) {
        this.queued = false;
        await this.pass();
      }
    } finally {
      this.running = false;
    }
  }

  /** Keep stepping while each step makes progress toward the tail. */
  private async pass(): Promise<void> {
    // One pass gets to act on a tail hole, whatever it decides to do with it.
    let tailHole = this.tailHole;
    this.tailHole = false;

    for (;;) {
      const step = planSync(this.state, tailHole);
      tailHole = false;
      if (step === null) {
        return;
      }
      if (!(await this.execute(step))) {
        return;
      }
    }
  }

  /** Run one step. Returns whether the pass should keep going. */
  private async execute(step: SyncStep): Promise<boolean> {
    switch (step.kind) {
      case 'initial':
        this.reset({ type: 'OPEN', anchor: step.around });
        await this.page('initial', buildInitialCommentUrl(this.objectKey, step.around));
        // A fresh window is complete by construction, so never chain off one.
        return false;

      case 'newer': {
        const response = await this.page('newer', buildNewerCommentUrl(this.objectKey, step.after));
        // A full page may not be the last. A short one ends the catch-up,
        // whatever the snapshot now says about `hasNewer`.
        return response !== null && response.items.length === COMMENT_PAGE_SIZE;
      }

      case 'summary': {
        const snapshot = await this.requestSummary();
        // The summary is an empty topic's only probe: once it reports a tail, the
        // next plan loads the window. A history refresh cannot move a window, so
        // it never chains.
        return snapshot !== null && this.state.loadedTo === null && snapshot.last_id !== null;
      }
    }
  }

  private rebuild(anchor: number | null): Promise<boolean> {
    return this.execute({ kind: 'initial', around: anchor });
  }

  /**
   * Discard the window. The only place that aborts pages: abort first, so a
   * drained request dispatches nothing, then dispatch, because these are the
   * actions that release the request slots.
   */
  private reset(action: WindowAction): void {
    for (const controller of Object.values(this.pages)) {
      controller?.abort();
    }
    this.pages = {};
    this.summary?.abort();
    this.summary = null;
    this.dispatch(action);
  }

  // -- requests ------------------------------------------------------------

  private async page(edge: Edge, url: string): Promise<PageResponse | null> {
    if (this.pages[edge]) {
      return null;
    }

    const controller = new AbortController();
    this.pages[edge] = controller;
    this.dispatch({ type: 'REQUEST', edge });
    const args: RequestArgs = { url, method: 'GET', abortController: controller };

    try {
      const response = await api.request<PageResponse, unknown>(args);
      if (!response.ok) {
        throw response.error ?? requestError(args);
      }
      if (controller.signal.aborted) {
        return null;
      }
      this.dispatch({ type: 'PAGE', edge, response: response.data });
      return response.data;
    } catch (error) {
      if (!controller.signal.aborted) {
        this.dispatch({ type: 'FAIL', edge, error: error instanceof Error ? error : requestError(args) });
      }
      return null;
    } finally {
      // Release before pumping, or the pass this drains still looks busy.
      if (this.pages[edge] === controller) {
        delete this.pages[edge];
      }
      this.pump();
    }
  }

  /**
   * A metadata-only refresh. It deliberately owns no `Edge`: it is not a page,
   * and borrowing the newer edge would spin that button, surface a retry prompt
   * nobody asked for, and single-flight against the reader's own click. A
   * failure is silent — the next trigger runs it again.
   */
  private async requestSummary(): Promise<SummaryResponse | null> {
    if (this.summary) {
      return null;
    }

    const controller = new AbortController();
    this.summary = controller;
    const args: RequestArgs = {
      url: buildCommentSummaryUrl(this.objectKey),
      method: 'GET',
      abortController: controller,
    };

    try {
      const response = await api.request<SummaryResponse, unknown>(args);
      if (!response.ok || controller.signal.aborted) {
        return null;
      }
      this.dispatch({ type: 'SEED', topic: response.data });
      return response.data;
    } catch {
      return null;
    } finally {
      if (this.summary === controller) {
        this.summary = null;
      }
      this.pump();
    }
  }

  // -- reader actions ------------------------------------------------------

  loadOlder = async (): Promise<void> => {
    const { loadedFrom } = this.state;
    if (!hasOlder(this.state) || loadedFrom === null || this.pages.older) {
      return;
    }
    await this.page('older', buildOlderCommentUrl(this.objectKey, loadedFrom));
  };

  loadNewer = async (): Promise<void> => {
    const { loadedTo } = this.state;
    if (!hasNewer(this.state) || loadedTo === null || this.pages.newer) {
      return;
    }
    await this.page('newer', buildNewerCommentUrl(this.objectKey, loadedTo));
  };

  retry = async (edge: Edge): Promise<void> => {
    const { loadedFrom, loadedTo, anchor } = this.state;
    if (edge === 'older' && loadedFrom !== null) {
      await this.page('older', buildOlderCommentUrl(this.objectKey, loadedFrom));
    } else if (edge === 'newer' && loadedTo !== null) {
      // Not `loadNewer`: a failed background catch-up leaves an error on an edge
      // the snapshot says has nothing newer, and it still deserves a retry.
      await this.page('newer', buildNewerCommentUrl(this.objectKey, loadedTo));
    } else {
      await this.rebuild(anchor);
    }
  };

  create = async (content: MarkdownWrite): Promise<void> => {
    if (this.creating) {
      return;
    }
    const wasAtEnd = atEnd(this.state);
    const controller = new AbortController();
    this.creating = controller;
    const args: RequestArgs = {
      url: buildCommentUrl(this.objectKey),
      method: 'POST',
      body: { content },
      abortController: controller,
    };

    try {
      const response = await api.request<CreateResponse, unknown>(args);
      if (!response.ok) {
        throw response.error ?? requestError(args);
      }
      if (controller.signal.aborted) {
        return;
      }
      this.dispatch({
        type: 'CREATED',
        comment: response.data.comment,
        prevId: response.data.prev_id,
        topic: response.data,
      });
      if (!wasAtEnd) {
        // Submitting from history rebuilds at the tail, never at the anchor: the
        // point of the rebuild is to render the comment just created.
        await this.rebuild(null);
      }
    } finally {
      if (this.creating === controller) {
        this.creating = null;
      }
      this.pump();
    }
  };

  remove = async (id: number): Promise<void> => {
    const args: RequestArgs = { url: buildDeleteCommentUrl(this.objectKey, id), method: 'DELETE' };
    const response = await api.request<DeleteResponse, unknown>(args);
    if (!response.ok) {
      throw response.error ?? requestError(args);
    }
    // The response and channel echo are the same versioned fact. Either may win.
    this.dispatch({ type: 'DELETED', id: response.data.id, topic: response.data });
  };

  // -- channel events ------------------------------------------------------

  onCreated = ({ data }: RealtimeEvent<CreateResponse>): void => {
    const current = this.state;
    // An out-of-order create is positive proof the tail has a hole, even though
    // the fresher snapshot it carries says the window is no longer at the end.
    const adjacencyFailure =
      current.loadedTo !== null &&
      atEnd(current) &&
      !current.loading.initial &&
      data.comment.id > current.loadedTo &&
      data.prev_id !== current.loadedTo;

    this.dispatch({ type: 'CREATED', comment: data.comment, prevId: data.prev_id, topic: data });
    if (adjacencyFailure) {
      this.tailHole = true;
      this.sync();
    }
  };

  onDeleted = ({ data }: RealtimeEvent<DeleteResponse>): void => {
    this.dispatch({ type: 'DELETED', id: data.id, topic: data });
  };
}
